// Mutation avec état, message de succès affiché seulement après la réponse du serveur, et
// invalidation des requêtes concernées.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ErreurApi, messageErreur } from '../api/erreurs';
import { useMessages } from './Messages';

export interface OptionsAction<E, R> {
  executer: (entree: E) => Promise<R>;
  succes?: string | ((r: R, entree: E) => string);
  invalider?: readonly (readonly unknown[])[];
  apres?: (r: R, entree: E) => void;
}

export function useAction<E, R>({ executer, succes, invalider = [], apres }: OptionsAction<E, R>) {
  const client = useQueryClient();
  const { afficher } = useMessages();
  const m = useMutation<R, unknown, E>({
    mutationFn: executer,
    onSuccess: async (r, entree) => {
      // Une requête encore à son premier chargement serait reprise telle quelle par l'invalidation (TanStack
      // Query n'interrompt un chargement en cours que si la requête a déjà des données) et afficherait l'état
      // d'avant l'action ; elle est donc interrompue d'abord, puis relancée après l'écriture.
      await Promise.all(invalider.map((cle) => client.cancelQueries({ queryKey: cle })));
      await Promise.all(invalider.map((cle) => client.invalidateQueries({ queryKey: cle })));
      if (succes) afficher('succes', typeof succes === 'function' ? succes(r, entree) : succes);
      apres?.(r, entree);
    },
  });
  const erreur = m.error;
  return {
    lancer: (entree: E) => m.mutate(entree),
    lancerAsync: (entree: E) => m.mutateAsync(entree),
    enCours: m.isPending,
    erreur,
    messageErreur: erreur ? messageErreur(erreur) : null,
    /** Erreurs de validation par champ (400). */
    erreursChamps: erreur instanceof ErreurApi ? erreur.champs : [],
    reinitialiser: m.reset,
  };
}
